/**
 * Reine Helfer des CardDetailModal — ohne React, damit die Gruppierung des Besitzes und die
 * Kartenseiten für sich prüfbar sind.
 */
export const SECTIONS = [
  { id: 'card', label: 'Karte' },
  { id: 'prints', label: 'Alle Prints' },
  { id: 'ownership', label: 'Im Besitz' },
  { id: 'decks', label: 'Virtuelle Decks' },
  { id: 'friends', label: 'Freunde' },
];

export const RARITY_LABELS = {
  common: 'Common', uncommon: 'Uncommon', rare: 'Rare', mythic: 'Mythic', special: 'Special', bonus: 'Bonus',
};

export const CONDITION_CODES = {
  near_mint: 'NM', excellent: 'EX', good: 'GD', light_played: 'LP', played: 'PL', poor: 'PO',
};

export function languageCode(language) {
  return String(language || 'en').toUpperCase();
}

export function conditionCode(condition) {
  return CONDITION_CODES[condition] ?? String(condition || '').toUpperCase();
}

export function formatEuro(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return `${Number(value).toFixed(2).replace('.', ',')} €`;
}

export function priceFor(print, foil) {
  if (!print) return null;
  const value = foil === 'foil' || foil === 'etched' ? print.priceEuroFoil : print.priceEuro;
  return value === null || value === undefined ? null : value;
}

// Gleiche Reihenfolge wie der Server (released_at DESC NULLS LAST, dann id).
export function comparePrints(a, b) {
  const da = a?.releasedAt || null;
  const db = b?.releasedAt || null;
  if (da !== db) {
    if (da === null) return 1;
    if (db === null) return -1;
    return da < db ? 1 : -1;
  }
  return String(a?.scryfallId).localeCompare(String(b?.scryfallId));
}

/**
 * Besitz-Zeilen (eigene oder Freunde) → ein Block je Print mit den Listen „In Bindern“ und
 * „In Decks“. Zusammengezählt wird je Besitzer + binderId + Sprache + Foil + Zustand: ein
 * physisches Deck trägt je Kategorie eine eigene Zeile, gleichnamige Binder bleiben getrennt.
 * Besitz an einem Print, den `printById` nicht kennt, fällt nicht weg (Block mit Minimal-Print).
 */
export function buildPrintBlocks(rows, printById) {
  const blocks = new Map();
  for (const row of rows || []) {
    const print = printById.get(row.scryfallId) ?? { scryfallId: row.scryfallId };
    const block = blocks.get(row.scryfallId) ?? { print, lines: new Map() };
    const ownerName = row.friendName !== undefined ? row.friendName : null;
    const key = `${ownerName === null ? '' : row.userId}||${row.binderId}||${row.language}||${row.foil}||${row.condition}`;
    const line = block.lines.get(key) ?? {
      key, ownerName, quantity: 0, foil: row.foil, language: row.language, condition: row.condition,
      price: priceFor(print, row.foil), binderName: row.binderName, inDeck: row.binderType === 'deck',
    };
    line.quantity += row.quantity;
    block.lines.set(key, line);
    blocks.set(row.scryfallId, block);
  }
  const byOwnerThenBinder = (a, b) => (a.ownerName ?? '').localeCompare(b.ownerName ?? '') || a.binderName.localeCompare(b.binderName) || a.key.localeCompare(b.key);
  return [...blocks.values()]
    .sort((a, b) => comparePrints(a.print, b.print))
    .map(({ print, lines }) => {
      const all = [...lines.values()].sort(byOwnerThenBinder);
      const strip = ({ inDeck, ...line }) => line;
      return {
        print,
        binders: all.filter((l) => !l.inDeck).map(strip),
        decks: all.filter((l) => l.inDeck).map(strip),
      };
    });
}

function faceStats(face) {
  if (face.power != null && face.toughness != null) return `${face.power}/${face.toughness}`;
  if (face.loyalty != null) return `Loyalität ${face.loyalty}`;
  if (face.defense != null) return `Verteidigung ${face.defense}`;
  return null;
}

const hasFaces = (card) => Array.isArray(card?.faces) && card.faces.length > 0;

/**
 * Kartenseiten für den Abschnitt „Karte“: je Seite { name, manaCost, typeLine, text, stats }.
 * Die Seiten kommen aus `card.faces` (eine Zeile je Scryfall-Seite): Name, Kosten, Typzeile,
 * Text und Werte (Stärke/Widerstand, Loyalität, Verteidigung) je Seite direkt aus den Daten.
 * Deutsch nur mit `card.de`; die deutschen Werte kommen aus `card.de.faces`, je Feld mit
 * Rückfall auf die englische Seite. Ohne `de.faces` (kein deutscher Druck mit Seiten) bleiben
 * alle Seitenwerte englisch — der gedruckte Name aus `de.printedName` gehört zur ganzen Karte
 * und lässt sich keiner Seite zuordnen.
 *
 * Ohne `card.faces` (Druck ohne Karten-Zeile, „Unbekannte Karte“) eine Seite aus den
 * Karten-Feldern; deutsch mit `de.printedName`/`de.printedTypeLine`, wenn vorhanden, und dem
 * Text aus `de.oracleText`.
 */
export function cardFaces(card, lang) {
  if (!card) return [];
  const de = lang === 'de' ? card.de : null;
  if (lang === 'de' && !de) return [];
  if (!hasFaces(card)) {
    const stats = card.power != null && card.toughness != null ? `${card.power}/${card.toughness}` : null;
    return [{
      name: de?.printedName || card.name || '',
      manaCost: card.manaCost || null,
      typeLine: de?.printedTypeLine || card.typeLine || '',
      text: (de ? de.oracleText : card.oracleText) || null,
      stats,
    }];
  }
  const deFaces = Array.isArray(de?.faces) ? de.faces : [];
  return card.faces.map((face, i) => ({
    name: deFaces[i]?.printedName || face.name || '',
    manaCost: face.manaCost || null,
    typeLine: deFaces[i]?.printedTypeLine || face.typeLine || '',
    text: deFaces[i]?.printedText || face.oracleText || null,
    stats: faceStats(face),
  }));
}

/**
 * Gibt es deutschen Regeltext? Entscheidet über den EN/DE-Umschalter des Modals. Mit Seiten
 * zählt nur ein `printedText` in `de.faces`: `cardFaces` füllt fehlende deutsche Texte mit
 * Englisch auf, ein Umschalter darauf zeigte nur den englischen Text noch einmal.
 */
export function hasGermanRulesText(card) {
  if (!card?.de) return false;
  if (hasFaces(card)) return Array.isArray(card.de.faces) && card.de.faces.some((f) => f?.printedText);
  return !!card.de.oracleText;
}

const IMAGE_COL_MAX_PX = 360;
const IMAGE_COL_MIN_PX = 200;

/**
 * Breite der Bildspalte (Desktop): die Höhe, die unter Print-Infos, Links, Aktion und den
 * Abständen bleibt, im Kartenformat 5:7 — höchstens 360 px, mindestens 200 px. Ohne gemessene
 * Spaltenhöhe `null` (die CSS-Breite gilt). Mit `landscape` (Karte mit Querdarstellung) ist die
 * Spalte das Hochkant-Ergebnis × 7/5: die querliegende Karte füllt sie, die hochkant stehende
 * Seite steht in Hochkant-Breite mittig darin (Spec 2026-10-31 §6.2, E4).
 */
export function imageColumnWidth({ columnHeight, lowerHeight, gaps, landscape = false }) {
  if (!(columnHeight > 0)) return null;
  const fit = Math.floor(((columnHeight - lowerHeight - gaps) * 5) / 7);
  const portrait = Math.min(IMAGE_COL_MAX_PX, Math.max(IMAGE_COL_MIN_PX, fit));
  return landscape ? Math.round((portrait * 7) / 5) : portrait;
}
